import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Tab, Nav, Button, Spinner } from 'react-bootstrap';
import { FirebaseAuthContext } from 'context/FirebaseAuthContext';
import PageHeader from 'components/common/PageHeader';
import SummaryCards from './components/SummaryCards';
import FCTable from './components/FCTable';
import FilterBar from './components/FilterBar';
import ImportModal from './components/ImportModal';
import SalesCliffs from './components/SalesCliffs';
import SKUDetailModal from './modals/SKUDetailModal';
import ExportModal from './modals/ExportModal';
import { useFCRecommendations, DEFAULT_SORT_KEY } from './hooks/useFCRecommendations';
import { useFCBaseline } from './hooks/useFCBaseline';
import { toast } from 'react-toastify';
import './css/fcOptimizer.css';

export default function FCOptimizer() {
  const { user, authLoading } = useContext(FirebaseAuthContext);
  const navigate = useNavigate();

  // State
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSku, setSelectedSku] = useState(null);
  const [filters, setFilters] = useState({
    priority: 'all',
    search: '',
    recommendation: 'all',
    analysisType: 'ALL',  // Default to ALL (blended analysis from all order types)
    strategy: '',
    temperatureCategory: '',
    serviceLevel: ''
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState('recommendations');
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [bqSyncing, setBqSyncing] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // FC Method state - determines which FC value is displayed (SS1, SSN, MS, BLENDED)
  const [fcMethod, setFCMethod] = useState(() => {
    try {
      return localStorage.getItem('fc-optimizer-method') || 'SS1';
    } catch {
      return 'SS1';
    }
  });

  // Sort state with localStorage persistence
  const [sortKey, setSortKey] = useState(() => {
    try {
      return localStorage.getItem('fc-optimizer-sort') || DEFAULT_SORT_KEY;
    } catch {
      return DEFAULT_SORT_KEY;
    }
  });

  // Time frame state - default to 90 days (matches BigQuery scheduled query)
  const [timeFrame, setTimeFrame] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 90);
    return {
      preset: '90d',
      label: 'Last 90 Days',
      days: 90,
      startDate,
      endDate
    };
  });

  // Hooks
  const {
    recommendations,
    loading,
    error,
    counts,
    totalFilteredCount,
    refresh,
    currentPage,
    totalPages,
    goToPage,
    lastAnalysisRun,
    isRealTime,
    fetchAllForExport
  } = useFCRecommendations(filters, timeFrame, sortKey);

  // Format relative time for timestamp display
  const formatRelativeTime = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const {
    baseline,
    baselineLoading,
    uploadBaseline
  } = useFCBaseline();

  // Auth redirect
  useEffect(() => {
    if (authLoading) return;
    if (!user) return navigate('/');
  }, [authLoading, user, navigate]);

  // Handlers
  const handleRowClick = (sku) => {
    setSelectedSku(sku);
    setShowDetailModal(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleTimeFrameChange = (newTimeFrame) => {
    setTimeFrame(newTimeFrame);
  };

  const handleSortChange = (newSortKey) => {
    setSortKey(newSortKey);
    try {
      localStorage.setItem('fc-optimizer-sort', newSortKey);
    } catch {
      // Ignore localStorage errors
    }
  };

  // Handle FC method change and persist to localStorage
  const handleFCMethodChange = (newMethod) => {
    setFCMethod(newMethod);
    try {
      localStorage.setItem('fc-optimizer-method', newMethod);
    } catch {
      // Ignore localStorage errors
    }
  };

  const handleImportComplete = () => {
    setShowImportModal(false);
    refresh();
    toast.success('Baseline imported successfully');
  };

  const handleRunAnalysis = async () => {
    if (analysisRunning) return;

    setAnalysisRunning(true);
    toast.info('Starting FC analysis... This may take a few minutes.');

    try {
      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net'}/fcOptimizerRunAnalysis`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          `Analysis complete! Analyzed ${result.analyzed} SKUs. ` +
          `Urgent: ${result.byPriority?.URGENT || 0}, High: ${result.byPriority?.HIGH || 0}`
        );
        refresh(); // Refresh the data
      } else {
        toast.error(`Analysis failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error running analysis:', error);
      toast.error('Failed to run analysis. Check console for details.');
    } finally {
      setAnalysisRunning(false);
    }
  };

  const handleBQSync = async () => {
    if (bqSyncing) return;

    setBqSyncing(true);
    const daysLabel = timeFrame.label || `${timeFrame.days} days`;
    toast.info(`Syncing from BigQuery (${daysLabel})... This will pull pre-computed analysis for all order types.`);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net'}/fcOptimizerBQSyncManual`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            daysBack: timeFrame.days || 90  // Pass time horizon to backend
          })
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success(
          `BigQuery sync complete (${daysLabel})! ` +
          `Processed ${result.rowsProcessed} rows. ` +
          `Single SKU: ${result.byAnalysisType?.SINGLE_SKU || 0}, ` +
          `Multi SKU: ${result.byAnalysisType?.MULTI_SKU || 0}, ` +
          `All: ${result.byAnalysisType?.ALL || 0}`
        );
        refresh(); // Refresh the data to show new results
      } else {
        toast.error(`BigQuery sync failed: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error syncing from BigQuery:', error);
      toast.error('Failed to sync from BigQuery. Check console for details.');
    } finally {
      setBqSyncing(false);
    }
  };

  // Export functionality moved to ExportModal component

  // handleExportAll removed - functionality consolidated into ExportModal

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(recommendations.map(r => r.sku));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (sku, checked) => {
    if (checked) {
      setSelectedRows(prev => [...prev, sku]);
    } else {
      setSelectedRows(prev => prev.filter(s => s !== sku));
    }
  };

  if (authLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="FC Optimizer"
        description="Manage fulfillment costs and optimize margins"
        className="mb-3"
      />

      {/* Summary Cards */}
      <SummaryCards
        counts={counts}
        loading={loading}
        activePriority={filters.priority !== 'all' ? filters.priority : null}
        onPriorityClick={(priority) => handleFilterChange({ priority })}
      />

      {/* Main Content */}
      <Card className="mt-3">
        <Card.Header className="bg-light">
          <Row className="align-items-center">
            <Col>
              <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs">
                  <Nav.Item>
                    <Nav.Link eventKey="recommendations">Recommendations</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="quickActions">Quick Actions</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="salesCliffs">Sales Cliffs</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Tab.Container>
            </Col>
            <Col xs="auto" className="d-flex align-items-center">
              <span
                className="badge bg-secondary me-2"
                title={lastAnalysisRun?.runAt ? `Last analyzed: ${lastAnalysisRun.runAt.toLocaleString()}` : 'No analysis run yet'}
              >
                📊 {formatRelativeTime(lastAnalysisRun?.runAt)}
              </span>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                className="me-2"
                onClick={() => setShowImportModal(true)}
              >
                Import Current FC
              </Button>
              <Button
                variant="outline-warning"
                size="sm"
                className="me-2"
                onClick={handleRunAnalysis}
                disabled={analysisRunning}
                title="Re-analyze fulfillments and refresh recommendations"
              >
                {analysisRunning ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Analyzing...
                  </>
                ) : (
                  'Run Analysis'
                )}
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                className="me-2"
                onClick={handleBQSync}
                disabled={bqSyncing}
                title="Sync pre-computed analysis from BigQuery (includes all 3 order types)"
              >
                {bqSyncing ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-1" />
                    Syncing BQ...
                  </>
                ) : (
                  '↻ Sync BigQuery'
                )}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                onClick={refresh}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={recommendations.length === 0}
              >
                Export{selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
              </Button>
            </Col>
          </Row>
        </Card.Header>

        <Card.Body className="p-0">
          <Tab.Content>
            <Tab.Pane eventKey="recommendations" active={activeTab === 'recommendations'}>
              {/* Filter Bar */}
              <FilterBar
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedCount={selectedRows.length}
                timeFrame={timeFrame}
                onTimeFrameChange={handleTimeFrameChange}
                sortKey={sortKey}
                onSortChange={handleSortChange}
                fcMethod={fcMethod}
                onFCMethodChange={handleFCMethodChange}
              />

              {/* Table */}
              <FCTable
                data={recommendations}
                loading={loading}
                error={error}
                selectedRows={selectedRows}
                onRowClick={handleRowClick}
                onSelectAll={handleSelectAll}
                onRowSelect={handleRowSelect}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                sortKey={sortKey}
                onSortChange={handleSortChange}
                fcMethod={fcMethod}
              />
            </Tab.Pane>

            <Tab.Pane eventKey="quickActions" active={activeTab === 'quickActions'}>
              <div className="p-4 text-center text-muted">
                <p>Quick Actions will be available in Phase 2.</p>
                <p className="fs--1">
                  This feature consolidates urgent actions like sales cliffs,
                  no FC set, and FC too low alerts.
                </p>
              </div>
            </Tab.Pane>

            <Tab.Pane eventKey="salesCliffs" active={activeTab === 'salesCliffs'}>
              <SalesCliffs />
            </Tab.Pane>
          </Tab.Content>
        </Card.Body>
      </Card>

      {/* Modals */}
      <ImportModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onComplete={handleImportComplete}
      />

      <SKUDetailModal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        sku={selectedSku}
      />

      <ExportModal
        show={showExportModal}
        onHide={() => setShowExportModal(false)}
        filters={filters}
        timeFrame={timeFrame}
        selectedRows={selectedRows}
        recommendations={recommendations}
        totalFilteredCount={totalFilteredCount}
        onExportComplete={() => setSelectedRows([])}
        fetchAllForExport={fetchAllForExport}
        fcMethod={fcMethod}
      />
    </>
  );
}
